// frontend/src/pages/SalesJournal.js
// Weekly journaling for Sales EMCs. Pick a cycle → see one row per week in
// the cycle → click a week to edit progress / reflections / learnings.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getJournal, saveJournalEntry, deleteJournalEntry,
  getCycles, getUsers,
} from '../services/api';
import { Card, Avatar, Badge, Btn, Field, Select, Textarea, Spinner, SectionTitle } from '../components/UI';
import PageHeader from '../components/PageHeader';
// Week math lives in utils/week.js — it builds YYYY-MM-DD from LOCAL date parts
// so Mondays never shift a day via toISOString()'s UTC conversion (H9/M11).
import { mondaysBetween, currentMondayISO } from '../utils/week';

function fmtWeek(isoMonday) {
  const d = new Date(isoMonday + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function SalesJournal() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cycles,    setCycles]    = useState([]);
  const [users,     setUsers]     = useState([]);
  const [data,      setData]      = useState({ cycle: null, entries: [] });
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [editing,   setEditing]   = useState(null);   // { week_start, progress, reflections, learnings, id? }

  const isManager   = ['admin','manager'].includes(user?.role);
  const isAdmin     = user?.role === 'admin';
  const cycleId     = searchParams.get('cycle')    || '';
  const employeeId  = searchParams.get('employee') || String(user?.id || '');
  const viewingSelf = String(employeeId) === String(user?.id || '');

  // A week is editable if the user is admin (admins can backfill / correct
  // past entries on anyone), or if it's the viewer's own current/future week.
  // Past weeks of one's own journal are read-only — once the week is gone,
  // the entry is a record, not a draft.
  const todayMonday = currentMondayISO();
  const canEditWeek = (week) => {
    if (isAdmin) return true;
    if (!viewingSelf) return false;
    return week >= todayMonday;
  };

  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === '' || v == null) next.delete(k); else next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    Promise.all([
      getCycles({ program_id: 2 }).catch(() => []),
      isManager ? getUsers().catch(() => []) : Promise.resolve([]),
    ]).then(([c, u]) => {
      const cyc = Array.isArray(c) ? c : [];
      setCycles(cyc);
      setUsers(Array.isArray(u) ? u : []);
      if (!cycleId) {
        const active = cyc.find(x => x.status === 'active');
        if (active) setParam('cycle', String(active.id));
      }
    }).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const reload = () => {
    if (!cycleId || !employeeId) { setData({ cycle: null, entries: [] }); return; }
    getJournal(cycleId, employeeId)
      .then(d => setData(d || { cycle: null, entries: [] }))
      .catch(() => setData({ cycle: null, entries: [] }));
  };
  useEffect(reload, [cycleId, employeeId]); // eslint-disable-line

  const weeks = useMemo(() => {
    if (!data?.cycle) return [];
    return mondaysBetween(data.cycle.start_date, data.cycle.end_date);
  }, [data]);

  const entryByWeek = useMemo(() => {
    const m = {};
    (data.entries || []).forEach(e => { m[String(e.week_start).slice(0, 10)] = e; });
    return m;
  }, [data]);

  const openEditor = week => {
    const e = entryByWeek[week];
    setEditing({
      id:          e?.id || null,
      week_start:  week,
      progress:    e?.progress    || '',
      reflections: e?.reflections || '',
      learnings:   e?.learnings   || '',
    });
  };

  const saveEntry = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await saveJournalEntry({
        employee_id: employeeId,
        cycle_id:    cycleId,
        week_start:  editing.week_start,
        progress:    editing.progress,
        reflections: editing.reflections,
        learnings:   editing.learnings,
      });
      setEditing(null);
      reload();
    } catch (e) {
      alert(e.response?.data?.error || 'Error saving entry');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async () => {
    if (!editing?.id) { setEditing(null); return; }
    if (!window.confirm('Delete this week\'s journal entry?')) return;
    try {
      await deleteJournalEntry(editing.id);
      setEditing(null);
      reload();
    } catch (e) {
      alert(e.response?.data?.error || 'Error deleting entry');
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <PageHeader moduleKey="sales-journal" helpTask="sales-journal"
          subtitle={viewingSelf
            ? 'One entry per week across the cycle — progress, reflections, and key learnings.'
            : "Viewing this EMC's weekly journal."} />
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Cycle</label>
          <Select value={cycleId} onChange={e => setParam('cycle', e.target.value)} style={{ width: 260 }}>
            <option value="">Select cycle…</option>
            {cycles.map(c => <option key={c.id} value={c.id}>{c.name}{c.status === 'active' ? ' · Active' : ''}</option>)}
          </Select>
        </div>
        {isManager && (
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Employee</label>
            <Select value={employeeId} onChange={e => setParam('employee', e.target.value)} style={{ width: 260 }}>
              <option value={String(user?.id || '')}>Me</option>
              {users
                .filter(u => Number(u.program_id) === 2)
                .map(u => <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` — ${u.job_title}` : ''}</option>)}
            </Select>
          </div>
        )}
      </div>

      {/* Weeks */}
      {weeks.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-3)', fontSize: 13 }}>
            Pick a cycle to see the week list.
          </div>
        </Card>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)' }}>
                {['Week starting','Status','Progress preview','Actions'].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map(w => {
                const e = entryByWeek[w];
                return (
                  <tr key={w} style={{ borderBottom: '0.5px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{fmtWeek(w)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {e
                        ? <Badge color="green">Submitted</Badge>
                        : <Badge color="amber">Pending</Badge>}
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-3)', maxWidth: 420 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e?.progress || e?.reflections || e?.learnings || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <Btn variant="secondary" size="sm" onClick={() => openEditor(w)}>
                        {canEditWeek(w) ? (e ? 'Edit' : 'Write') : 'View'}
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Past entries feed — shows full content of every submitted entry in
          the selected cycle so EMCs can re-read what they've written without
          having to open each one from the table above. */}
      {data.entries?.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <SectionTitle>Your entries this cycle ({data.entries.length})</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
            {data.entries.map(e => (
              <Card key={e.id} style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Week of {fmtWeek(String(e.week_start).slice(0, 10))}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>
                      Last updated {e.updated_at ? new Date(e.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                  </div>
                  <Btn variant="secondary" size="sm" onClick={() => openEditor(String(e.week_start).slice(0, 10))}>
                    {canEditWeek(String(e.week_start).slice(0, 10)) ? 'Edit' : 'View'}
                  </Btn>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                  {[
                    { key: 'progress',    label: 'Progress',    color: 'var(--navy-600)',  text: e.progress },
                    { key: 'reflections', label: 'Reflections', color: 'var(--blue-700)',   text: e.reflections },
                    { key: 'learnings',   label: 'Learnings',   color: 'var(--amber-700)',  text: e.learnings },
                  ].map(section => (
                    <div key={section.key}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.6px', color: section.color, fontWeight: 500, marginBottom: 4 }}>{section.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.55, whiteSpace: 'pre-wrap', padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 6, minHeight: 44 }}>
                        {section.text || <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>— nothing captured here —</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty state — tell employees what to do if they haven't written anything */}
      {viewingSelf && weeks.length > 0 && (data.entries?.length ?? 0) === 0 && (
        <Card style={{ marginTop: 20, background: 'var(--navy-50)', border: '0.5px solid var(--navy-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 22 }}>📝</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy-600)' }}>No journal entries yet</div>
              <div style={{ fontSize: 12, color: 'var(--navy-600)', marginTop: 4, lineHeight: 1.5 }}>
                Pick a week above and click <strong>Write</strong> to capture your first entry. Each entry covers three short sections: progress, reflections, and key learnings.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Editor drawer — renders as a card below */}
      {editing && (() => {
        const editable    = canEditWeek(editing.week_start);
        const lockedReason = !editable
          ? (viewingSelf
              ? 'Past weeks are locked — this entry is read-only.'
              : 'Read-only — only the EMC can edit their own journal.')
          : null;
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setEditing(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto', padding: 24, boxShadow: 'var(--shadow-md)', margin: '0 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ fontFamily: "'Poppins', sans-serif" }}>Week of {fmtWeek(editing.week_start)}</h3>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{data.cycle?.name}</div>
              </div>
              <button onClick={() => setEditing(null)} type="button" style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-3)', cursor: 'pointer' }}>×</button>
            </div>
            {lockedReason && (
              <div style={{ background: 'var(--gray-50)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: 'var(--text-2)' }}>
                {lockedReason}
              </div>
            )}
            <Field label="Progress — what got done">
              <Textarea rows={5} value={editing.progress} readOnly={!editable} onChange={e => setEditing(s => ({ ...s, progress: e.target.value }))} placeholder="Deals advanced, meetings held, calls made, renewals secured…" />
            </Field>
            <Field label="Reflections — what's working / what's not">
              <Textarea rows={5} value={editing.reflections} readOnly={!editable} onChange={e => setEditing(s => ({ ...s, reflections: e.target.value }))} placeholder="What you learned about your pipeline, customers, or approach." />
            </Field>
            <Field label="Key learnings">
              <Textarea rows={4} value={editing.learnings} readOnly={!editable} onChange={e => setEditing(s => ({ ...s, learnings: e.target.value }))} placeholder="Things to take into next week; questions to raise at the GAS." />
            </Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 8 }}>
              <div>
                {editing.id && editable && (
                  <Btn type="button" variant="ghost" size="sm" onClick={deleteEntry} style={{ color: 'var(--red-500)' }}>Delete entry</Btn>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn type="button" variant="secondary" onClick={() => setEditing(null)}>{editable ? 'Cancel' : 'Close'}</Btn>
                {editable && <Btn type="button" onClick={saveEntry} disabled={saving}>{saving ? 'Saving…' : 'Save entry'}</Btn>}
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      <div style={{ marginTop: 24, fontSize: 11, color: 'var(--text-3)' }}>
        Entries are keyed to the Monday of the journaling week. You can write or edit only the current week — past weeks are read-only. Managers can view; admins can edit any week.
      </div>
    </div>
  );
}
